import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { supabase } from '../../supabase/client';

interface Classification {
  id: string;
  lead_n: number;
  first_name: string | null;
  last_name: string | null;
  linkedin_url: string | null;
  title: string | null;
  company: string | null;
  reply_text: string | null;
  intent_tier_raw: string | null;
  icp_fit: string | null;
  intent_tier_llm: string | null;
  is_icp_override: boolean;
  reasoning: string | null;
}

interface ReviewState {
  human_tier: string;
  human_icp_fit: string;
  notes: string;
  revealed: boolean;  // blind-first: model analysis hidden until user reveals
  saved: boolean;
}

interface ExistingReview {
  id: string;
  classification_id: string;
  human_tier: string;
  human_icp_fit: string;
  notes: string;
}

const TIER_LABELS: Record<string, { label: string; color: string }> = {
  A: { label: 'A — Concrete next step', color: 'text-green-700' },
  B: { label: 'B — Soft interest',      color: 'text-blue-700' },
  C: { label: 'C — Polite deferral',    color: 'text-yellow-700' },
  D: { label: 'D — Disqualified',       color: 'text-red-700' },
  E: { label: 'E — Noise / empty',      color: 'text-gray-500' },
};

const ICP_LABELS: Record<string, { label: string; color: string }> = {
  in_icp:  { label: 'In-ICP',  color: 'text-green-700' },
  off_icp: { label: 'Off-ICP', color: 'text-red-700' },
  unknown: { label: 'Unknown', color: 'text-gray-500' },
};

const CSRICPReview: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const navigate   = useNavigate();

  const [jobData, setJobData] = useState<{ company_name: string; campaign_name: string; status: string } | null>(null);
  const [records, setRecords]  = useState<Classification[]>([]);
  const [reviewMap, setReviewMap] = useState<Record<string, ReviewState>>({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState<string | null>(null); // id being saved
  const [error, setError]     = useState<string | null>(null);
  const [markingDone, setMarkingDone] = useState(false);

  useEffect(() => {
    if (jobId) loadData();
  }, [jobId]);

  async function loadData() {
    setLoading(true);
    setError(null);

    // Load job metadata
    const { data: job, error: jobErr } = await supabase
      .from('icp_jobs')
      .select('company_name, campaign_name, status')
      .eq('id', jobId)
      .single();
    if (jobErr || !job) { setError('Job not found'); setLoading(false); return; }
    setJobData(job);

    // Load icp_override records (the ones flagged for human review)
    const { data: recs, error: recErr } = await supabase
      .from('icp_classifications')
      .select('id, lead_n, first_name, last_name, linkedin_url, title, company, reply_text, intent_tier_raw, icp_fit, intent_tier_llm, is_icp_override, reasoning')
      .eq('job_id', jobId)
      .eq('is_icp_override', true)
      .order('lead_n');
    if (recErr) { setError(recErr.message); setLoading(false); return; }

    const overrideRecs = (recs ?? []) as Classification[];
    setRecords(overrideRecs);

    // Load existing reviews for these records
    const ids = overrideRecs.map(r => r.id);
    if (ids.length > 0) {
      const { data: existingReviews } = await supabase
        .from('icp_reviews')
        .select('id, classification_id, human_tier, human_icp_fit, notes')
        .in('classification_id', ids);

      const initialMap: Record<string, ReviewState> = {};
      for (const rec of overrideRecs) {
        const existing = (existingReviews ?? []).find((r: ExistingReview) => r.classification_id === rec.id);
        initialMap[rec.id] = {
          human_tier:    existing?.human_tier    ?? rec.intent_tier_raw ?? 'B',
          human_icp_fit: existing?.human_icp_fit ?? 'in_icp',
          notes:         existing?.notes         ?? '',
          revealed:      !!existing,
          saved:         !!existing,
        };
      }
      setReviewMap(initialMap);
    }

    setLoading(false);
  }

  function updateReview(id: string, patch: Partial<ReviewState>) {
    setReviewMap(prev => ({
      ...prev,
      [id]: { ...prev[id], ...patch, saved: false },
    }));
  }

  async function saveReview(rec: Classification) {
    const state = reviewMap[rec.id];
    if (!state) return;
    setSaving(rec.id);

    const { data: { user } } = await supabase.auth.getUser();
    const { error: upsertErr } = await supabase
      .from('icp_reviews')
      .upsert({
        classification_id: rec.id,
        reviewed_by:       user?.id,
        human_tier:        state.human_tier,
        human_icp_fit:     state.human_icp_fit,
        notes:             state.notes || null,
      }, { onConflict: 'classification_id' });

    if (upsertErr) {
      setError(`Failed to save review: ${upsertErr.message}`);
    } else {
      setReviewMap(prev => ({ ...prev, [rec.id]: { ...prev[rec.id], saved: true } }));
    }
    setSaving(null);
  }

  async function markReviewComplete() {
    if (!jobId) return;
    setMarkingDone(true);
    const { error: updateErr } = await supabase
      .from('icp_jobs')
      .update({ status: 'verdict_ready' })
      .eq('id', jobId);

    if (updateErr) {
      setError(`Failed to mark complete: ${updateErr.message}`);
      setMarkingDone(false);
    } else {
      navigate(`/csr-icp-signal/${jobId}/verdict`);
    }
  }

  const allSaved = records.every(r => reviewMap[r.id]?.saved);

  // ── Loading ───────────────────────────────────────────────────────────────
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-gray-400 text-center">
          <div className="text-3xl mb-2">⏳</div>
          <p>Loading review queue…</p>
        </div>
      </div>
    );
  }

  // ── No overrides ──────────────────────────────────────────────────────────
  if (records.length === 0) {
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <div className="max-w-2xl mx-auto">
          <button
            onClick={() => navigate('/csr-icp-signal')}
            className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1"
          >
            ← All Analyses
          </button>
          <div className="bg-white rounded-xl border border-gray-200 p-10 text-center">
            <div className="text-4xl mb-3">✅</div>
            <h2 className="text-lg font-semibold text-gray-800 mb-1">No records need review</h2>
            <p className="text-sm text-gray-500 mb-6">
              The model didn't flag any off-ICP-but-interested leads. You can go straight to the verdict.
            </p>
            <div className="flex gap-3 justify-center">
              <button
                onClick={markReviewComplete}
                disabled={markingDone}
                className="bg-green-600 text-white px-5 py-2 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-50"
              >
                {markingDone ? 'Loading…' : 'View Verdict →'}
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const savedCount   = records.filter(r => reviewMap[r.id]?.saved).length;

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <button
          onClick={() => navigate('/csr-icp-signal')}
          className="text-sm text-gray-500 hover:text-gray-700 mb-4 flex items-center gap-1"
        >
          ← All Analyses
        </button>

        <div className="mb-6">
          <h1 className="text-xl font-bold text-gray-900">Layer 2 Review — ICP Override Queue</h1>
          <p className="text-sm text-gray-500 mt-1">
            <strong>{jobData?.company_name}</strong> · {jobData?.campaign_name}
          </p>
          <p className="text-sm text-gray-500 mt-1">
            These {records.length} leads showed genuine interest but the AI flagged them as off-ICP.
            Review each one — did the AI get the ICP call right, or is your ICP definition too narrow?
          </p>
        </div>

        {/* Progress */}
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-6 flex justify-between items-center text-sm">
          <span className="text-amber-700">
            {savedCount} / {records.length} reviewed
          </span>
          <button
            onClick={markReviewComplete}
            disabled={!allSaved || markingDone}
            className="bg-green-600 text-white px-4 py-1.5 rounded text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {markingDone ? 'Saving…' : 'Mark Complete & View Verdict →'}
          </button>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4 text-sm text-red-700">{error}</div>
        )}

        {/* Record cards */}
        <div className="space-y-6">
          {records.map((rec, idx) => {
            const state   = reviewMap[rec.id] ?? { human_tier: 'B', human_icp_fit: 'in_icp', notes: '', revealed: false, saved: false };
            const isSaving = saving === rec.id;

            return (
              <div
                key={rec.id}
                className={`bg-white rounded-xl border-2 p-6 ${state.saved ? 'border-green-200' : 'border-gray-200'}`}
              >
                {/* Lead header */}
                <div className="flex justify-between items-start mb-3">
                  <div>
                    <span className="text-xs text-gray-400 font-mono mr-2">#{rec.lead_n}</span>
                    <span className="text-sm font-semibold text-gray-900">
                      {[rec.first_name, rec.last_name].filter(Boolean).join(' ') || 'Unknown'}
                    </span>
                    {state.saved && <span className="ml-2 text-xs text-green-600">✓ Saved</span>}
                  </div>
                  <span className="text-xs text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">ICP Override</span>
                </div>
                <div className="text-xs text-gray-500 mb-4">
                  {rec.title || 'Unknown title'} · {rec.company || 'Unknown company'}
                  {rec.linkedin_url && (
                    <a
                      href={rec.linkedin_url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="ml-2 text-indigo-500 hover:underline"
                    >
                      LinkedIn ↗
                    </a>
                  )}
                </div>

                {/* Reply (always visible — blind-first design) */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="text-xs font-medium text-gray-400 mb-1 uppercase tracking-wide">Reply</div>
                  <p className="text-sm text-gray-800 whitespace-pre-wrap">
                    {rec.reply_text?.trim() || <span className="italic text-gray-400">[no reply text]</span>}
                  </p>
                </div>

                {/* Model analysis — hidden until revealed */}
                {!state.revealed ? (
                  <button
                    onClick={() => updateReview(rec.id, { revealed: true })}
                    className="text-xs text-indigo-600 underline mb-4 block"
                  >
                    Reveal model analysis (make your own call first)
                  </button>
                ) : (
                  <div className="border border-indigo-100 rounded-lg p-3 mb-4 bg-indigo-50 text-xs">
                    <div className="font-medium text-indigo-700 mb-1">Model analysis</div>
                    <div className="text-gray-700 space-y-0.5">
                      <div>
                        Intent (raw):
                        <span className={`ml-1 font-semibold ${TIER_LABELS[rec.intent_tier_raw ?? '']?.color ?? ''}`}>
                          {rec.intent_tier_raw ?? '?'} — {TIER_LABELS[rec.intent_tier_raw ?? '']?.label}
                        </span>
                      </div>
                      <div>
                        ICP fit:
                        <span className={`ml-1 font-semibold ${ICP_LABELS[rec.icp_fit ?? '']?.color ?? ''}`}>
                          {ICP_LABELS[rec.icp_fit ?? '']?.label ?? rec.icp_fit}
                        </span>
                      </div>
                      {rec.reasoning && (
                        <div className="text-gray-600 mt-1 italic">{rec.reasoning}</div>
                      )}
                    </div>
                  </div>
                )}

                {/* Human review form */}
                <div className="grid grid-cols-2 gap-4 mb-3">
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Your intent call</label>
                    <select
                      value={state.human_tier}
                      onChange={e => updateReview(rec.id, { human_tier: e.target.value })}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      {Object.entries(TIER_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-1">Your ICP call</label>
                    <select
                      value={state.human_icp_fit}
                      onChange={e => updateReview(rec.id, { human_icp_fit: e.target.value })}
                      className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-indigo-400"
                    >
                      {Object.entries(ICP_LABELS).map(([k, v]) => (
                        <option key={k} value={k}>{v.label}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <textarea
                  value={state.notes}
                  onChange={e => updateReview(rec.id, { notes: e.target.value })}
                  placeholder="Notes (optional) — e.g. 'recruiter managing candidates via WhatsApp = behavioral in-ICP'"
                  rows={2}
                  className="w-full border border-gray-300 rounded px-2 py-1.5 text-xs mb-3 focus:outline-none focus:ring-2 focus:ring-indigo-400 resize-none"
                />

                <div className="flex justify-between items-center">
                  <span className="text-xs text-gray-400">
                    {idx + 1} of {records.length}
                  </span>
                  <button
                    onClick={() => saveReview(rec)}
                    disabled={isSaving}
                    className="bg-indigo-600 text-white px-4 py-1.5 rounded text-xs font-medium hover:bg-indigo-700 disabled:opacity-50"
                  >
                    {isSaving ? 'Saving…' : 'Save Review'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* Bottom CTA */}
        <div className="mt-8 pt-6 border-t border-gray-200 flex justify-end gap-3">
          <button
            onClick={markReviewComplete}
            disabled={!allSaved || markingDone}
            className="bg-green-600 text-white px-6 py-2.5 rounded-lg text-sm font-medium hover:bg-green-700 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            {markingDone ? 'Saving…' : 'Mark Complete & View Verdict →'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CSRICPReview;
